import { useState, useEffect } from 'react'
import CLUB from '../../config/club.js'
import { PBCC_PREMIUM, HUSTLER } from '../../config/siteMode.js'
import { watchMyResults, loadResult, onMemberChange, getMemberId } from '../../lib/memberGgc.js'

// History — a simple log of finished games. One row per completed game: game
// type, who played, and the date; newest first. No stored scorecards shown, no
// money, no reopening results (rows are non-tappable). Mirrors the companion
// app's History tab.
//
// Reads ONLY the data the site already has: watchMyResults gives the member's
// own /memberResults index (title=game type, savedAt=date, already newest-first)
// and loadResult reads the existing /ggResults/{id} snapshot for players[].name.
// No new store, no recompute, no scoring/settlement touched.
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const TITLE_ON_DARK = HUSTLER ? 'var(--ge-primary)' : (PBCC_PREMIUM ? 'var(--ge-gold)' : PRIMARY)
const SUB_ON_DARK = HUSTLER ? '#4b5563' : (PBCC_PREMIUM ? '#D8CFB8' : '#6B7280')

const fmtDate = (ms) => {
  if (!ms) return ''
  try { return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return '' }
}

export default function GHistory() {
  const [uid, setUid] = useState(getMemberId())
  const [rows, setRows] = useState([])
  // id → "Mark, Tom, Dave" (players live in the full snapshot, not the index).
  const [playersById, setPlayersById] = useState({})

  useEffect(() => onMemberChange(setUid), [])
  useEffect(() => {
    if (!uid) { setRows([]); return undefined }
    return watchMyResults(setRows)
  }, [uid])

  // Fetch each finished game's players once (read-only) for its row subtitle.
  useEffect(() => {
    let alive = true
    const missing = rows.filter((r) => playersById[r.id] === undefined)
    if (!missing.length) return undefined
    Promise.all(missing.map(async (r) => {
      const rec = await loadResult(r.id)
      const names = Array.isArray(rec?.players) ? rec.players.map((p) => p.name).filter(Boolean).join(', ') : ''
      return [r.id, names]
    })).then((pairs) => {
      if (!alive) return
      setPlayersById((prev) => {
        const next = { ...prev }
        pairs.forEach(([id, names]) => { next[id] = names })
        return next
      })
    }).catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold" style={{ color: TITLE_ON_DARK }}>History</h1>
        <p className="text-sm mt-1" style={{ color: SUB_ON_DARK }}>Every game you&apos;ve finished — newest first.</p>
      </div>

      {!uid ? (
        <p className="text-sm" style={{ color: SUB_ON_DARK }}>Sign in to see your history.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm" style={{ color: SUB_ON_DARK }}>No games yet — finish a game and it&apos;ll show up here.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl p-4"
              style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44` }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl leading-none">{r.icon || '⛳'}</span>
                  <div className="min-w-0">
                    <div className="text-base font-extrabold truncate" style={{ color: PRIMARY }}>{r.title || r.gameId || 'Game'}</div>
                    <div className="text-sm text-gray-600 mt-0.5 truncate">{playersById[r.id] || '…'}</div>
                  </div>
                </div>
                <span className="text-sm font-semibold whitespace-nowrap" style={{ color: '#6B7280' }}>{fmtDate(r.savedAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
